<template>
  <section class="faq standalone">
    <client-only>
      <algolia-search :categories="ALGOLIA_CATEGORIES.customer" />
    </client-only>

    <br /><br />
    <article v-if="faq">
      <h1 class="question">
        {{ faq.question }}
      </h1>
      <div v-thtml="faq.answer" class="answer" />
    </article>
  </section>
</template>

<script setup lang="ts">
import ALGOLIA_CATEGORIES from '~/json/algoliaCategories.json'
import { useAsyncData, useRoute } from 'nuxt/app'
import { computed } from 'vue'
import { getFaqMachine } from '../inversify.config'
import { FaqStandalone } from '../schemas'
import SCHEMA_ORGANIZATION from '../../../json/schemaOrganization.json'

const props = defineProps<{ slug: string; url: string }>()
const route = useRoute()
const lang = route.params.lang || 'en-gb'
const { data: faq } = useAsyncData(
  `faq-standalone-${props.slug}-${lang}`,
  getFaq,
)

function getFaq() {
  const faqMachine = getFaqMachine(route.params.lang || 'en-gb')
  return new Promise<FaqStandalone | null>((resolve) => {
    faqMachine
      .onTransition((state) => {
        if (
          (state.changed && state.context.standalone) ||
          state.matches('error')
        ) {
          resolve(state.context.standalone || null)
        }
      })
      .start()
      .send('FETCH_STANDALONE', { slug: props.slug })
  })
}

const headData = computed(() => {
  // Guard against null (error state from fetch failure)
  // useAsyncData blocks navigation until data loads, so no undefined state during CSR
  if (!faq.value) {
    return {}
  }
  return {
    title: `${faq.value.question} | Pitchup.com`,
    meta: [
      {
        hid: 'description',
        name: 'description',
        property: 'og:description',
        content: faq.value.description,
      },
    ],
    script: [
      {
        type: 'application/ld+json',
        body: true,
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: {
            '@type': 'Question',
            name: faq.value.question,
            answerCount: 1,
            author: {
              '@type': 'Organization',
              name: 'Pitchup.com',
            },
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.value.answer,
              url: props.url,
              author: {
                '@type': 'Organization',
                name: 'Pitchup.com',
              },
            },
          },
        }),
      },
      {
        type: 'application/ld+json',
        body: true,
        innerHTML: JSON.stringify(SCHEMA_ORGANIZATION),
        hid: 'schema-organization',
      },
    ],
  }
})

useHead(headData)
</script>

<style scoped>
.faq {
  padding: 1em 1em 7em 1em;
}

.question {
  line-height: 1.3;
  font-size: 1.5em;
  font-weight: 500;
}

article {
  min-height: 90vh;
  max-width: calc(100vw - 2em);
  overflow: hidden;
}

.faq .answer {
  font-size: var(--body);
}

.faq .answer :deep(p) {
  line-height: 1.4;
}

.answer :deep(ul) {
  margin-bottom: 1em;
}

.answer :deep(li) {
  display: list-item;
  list-style: disc inside;
}
</style>
